import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material";
import {
  ChevronRight,
  Folder,
  FolderOpen,
  FolderPlus,
  Home,
  Pencil,
} from "lucide-react";
import { useTranslationApp } from "../../lib/i18next";
import {
  GALLERY_FOLDERS_ALL,
  GALLERY_FOLDERS_BAR_ARIA_LABEL,
  GALLERY_FOLDERS_NEW_INLINE,
  GALLERY_FOLDERS_RENAME_ARIA_LABEL,
} from "../../i18n/keysTranslations";
import { GALLERY_DRAG_MIME_SELECTED } from "../../utils/constants";
import { getBreadcrumb, getChildFolders } from "../../utils/contentFolderUtils";

const folderBarScrollSx = {
  display: "flex",
  alignItems: "center",
  width: "100%",
  minHeight: 36,
  gap: 0,
  overflowX: "auto",
  overflowY: "hidden",
  pb: 1.25,
  scrollbarWidth: "thin",
  scrollbarColor: "#e0e0e0 transparent",
  "&::-webkit-scrollbar": {
    height: 5,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#e0e0e0",
    borderRadius: 999,
  },
};

const GalleryFolderBar = ({
  folders = [],
  activeFolderID = null,
  selectedContentIDs = [],
  canEdit = false,
  isLoading = false,
  onSelectFolder = () => {},
  onOpenCreateFolder = () => {},
  onDropMany = () => {},
  onRenameFolder = () => {},
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const [dragOverID, setDragOverID] = useState(undefined);

  const breadcrumb = useMemo(
    () => getBreadcrumb({ folders, folderID: activeFolderID }),
    [activeFolderID, folders]
  );

  const visibleFolders = useMemo(
    () => getChildFolders({ folders, parentFolderID: activeFolderID }),
    [activeFolderID, folders]
  );

  const selectedCount = selectedContentIDs.length;

  const resolveContentIDs = (event) => {
    const isPartOfSelection =
      event.dataTransfer.getData(GALLERY_DRAG_MIME_SELECTED) === "1";
    const draggedID = event.dataTransfer.getData("text/plain");

    if (!draggedID) return [];

    if (isPartOfSelection && selectedCount > 1) {
      return selectedContentIDs;
    }

    return [draggedID];
  };

  const onDragOver = (event, folderID) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDragOverID(folderID);
  };

  const onDragLeaveTarget = (event) => {
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && event.currentTarget.contains(relatedTarget)) {
      return;
    }
    setDragOverID(undefined);
  };

  const onDrop = (event, folderID) => {
    event.preventDefault();
    setDragOverID(undefined);

    const contentIDs = resolveContentIDs(event);
    if (contentIDs.length === 0) return;

    onDropMany({ contentIDs, folderID });
  };

  const getDropCountLabel = () => (selectedCount > 1 ? selectedCount : 1);

  const getDropBadgeSx = ({ isDragTarget }) => ({
    position: "absolute",
    top: -6,
    right: -4,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10,
    px: 0.75,
    py: 0.25,
    borderRadius: 10,
    bgcolor: "primary.main",
    color: "common.white",
    fontWeight: 700,
    lineHeight: 1.2,
    pointerEvents: "none",
    opacity: isDragTarget ? 1 : 0,
    visibility: isDragTarget ? "visible" : "hidden",
    transition: "opacity 0.15s ease",
    zIndex: 1,
  });

  const getBreadcrumbItemSx = ({ isActive, isDragTarget }) => ({
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    px: isActive ? 1.25 : 0.5,
    py: 0.75,
    borderRadius: 3,
    fontSize: 12,
    fontWeight: isActive ? 600 : 500,
    whiteSpace: "nowrap",
    border: isActive || isDragTarget ? "1px solid" : "none",
    transition: "all 0.15s ease",
    flexShrink: 0,
    bgcolor: "transparent",
    ...(isDragTarget
      ? {
          bgcolor: "primary.50",
          borderColor: "transparent",
          color: "primary.dark",
          transform: "scale(1.03)",
          boxShadow: 2,
          outline: "none",
        }
      : isActive
        ? {
            background: theme.palette.button.primary.main,
            borderColor: "transparent",
            color: theme.palette.button.primary.contrastText,
          }
        : {
            color: "grey.600",
            "&:hover": {
              color: "grey.900",
              bgcolor: "grey.100",
            },
          }),
  });

  const getFolderChipSx = ({ isActive, isDragTarget }) => ({
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    px: 1.25,
    py: 0.75,
    borderRadius: 3,
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: "nowrap",
    border: "1px solid",
    transition: "all 0.15s ease",
    flexShrink: 0,
    ...(isDragTarget
      ? {
          bgcolor: "primary.50",
          borderColor: "transparent",
          color: "primary.dark",
          transform: "scale(1.03)",
          boxShadow: 2,
          outline: "none",
        }
      : isActive
        ? {
            background: theme.palette.button.primary.main,
            borderColor: "transparent",
            color: theme.palette.button.primary.contrastText,
          }
        : {
            bgcolor: "common.white",
            borderColor: "grey.300",
            color: "grey.800",
            "&:hover": {
              borderColor: "grey.500",
              bgcolor: "grey.50",
            },
          }),
  });

  const getCreateFolderButtonSx = () => ({
    width: 30,
    height: 30,
    minWidth: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    p: 0,
    boxSizing: "border-box",
    borderRadius: 3,
    border: "1px dashed",
    borderColor: "grey.300",
    bgcolor: "common.white",
    color: "grey.600",
    cursor: isLoading ? "not-allowed" : "pointer",
    opacity: isLoading ? 0.5 : 1,
    transition: "all 0.15s ease",
    "&:hover": {
      borderColor: "grey.500",
      bgcolor: "grey.50",
      color: "grey.900",
    },
    "&:disabled": {
      pointerEvents: "none",
    },
  });

  return (
    <Box aria-label={t(GALLERY_FOLDERS_BAR_ARIA_LABEL)} sx={folderBarScrollSx}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.25,
          flexShrink: 0,
          mr: 1.5,
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => onSelectFolder(null)}
          onDragOver={(event) => onDragOver(event, null)}
          onDragLeave={onDragLeaveTarget}
          onDrop={(event) => onDrop(event, null)}
          sx={{
            ...getBreadcrumbItemSx({
              isActive: activeFolderID === null,
              isDragTarget: dragOverID === null,
            }),
            cursor: "pointer",
          }}
        >
          <Home size={12} />
          <Box component="span" sx={{ mt: 0.2 }}>
            {" "}
            {t(GALLERY_FOLDERS_ALL)}
          </Box>
        </Box>

        {breadcrumb.map((folder) => {
          const isActive = activeFolderID === folder.id;

          return (
            <Box
              key={folder.id}
              sx={{ display: "flex", alignItems: "center", gap: 0.25 }}
            >
              <ChevronRight size={12} color="#9ca3af" />
              <Box
                component="button"
                type="button"
                onClick={() => onSelectFolder(folder.id)}
                sx={{
                  ...getBreadcrumbItemSx({ isActive, isDragTarget: false }),
                  cursor: "pointer",
                }}
              >
                {folder.name}
              </Box>
            </Box>
          );
        })}
      </Box>

      {visibleFolders.length > 0 && (
        <Box
          sx={{
            width: "1px",
            height: 30,
            bgcolor: "grey.300",
            flexShrink: 0,
            mr: 1.5,
          }}
        />
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        {visibleFolders.map((folder) => {
          const isActive = activeFolderID === folder.id;
          const isDragTarget = dragOverID === folder.id;
          const dropCountLabel = getDropCountLabel();

          return (
            <Box
              key={folder.id}
              onDragOver={(event) => onDragOver(event, folder.id)}
              onDragLeave={onDragLeaveTarget}
              onDrop={(event) => onDrop(event, folder.id)}
              sx={{
                ...getFolderChipSx({ isActive, isDragTarget }),
                position: "relative",
                ...(canEdit
                  ? {
                      "&:hover .folder-rename-btn": {
                        width: 18,
                        minWidth: 18,
                        opacity: 1,
                        ml: 0.5,
                        pointerEvents: "auto",
                      },
                    }
                  : {}),
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => onSelectFolder(folder.id)}
                aria-label={folder.name}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  flex: 1,
                  minWidth: 0,
                  p: 0,
                  m: 0,
                  border: "none",
                  bgcolor: "transparent",
                  color: "inherit",
                  font: "inherit",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                {isActive ? (
                  <FolderOpen size={14} color="currentColor" />
                ) : (
                  <Folder
                    size={14}
                    color={isDragTarget ? "#3b82f6" : "#000000"}
                  />
                )}
                <Box component="span" sx={{ mt: 0.2 }}>
                  {folder.name}
                </Box>
              </Box>
              <Box
                component="span"
                aria-hidden={!isDragTarget}
                sx={getDropBadgeSx({ isDragTarget })}
              >
                +{dropCountLabel}
              </Box>
              {canEdit ? (
                <Box
                  component="button"
                  type="button"
                  className="folder-rename-btn"
                  aria-label={t(GALLERY_FOLDERS_RENAME_ARIA_LABEL, {
                    name: folder.name,
                  })}
                  onClick={(event) => {
                    event.stopPropagation();
                    onRenameFolder(folder);
                  }}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 0,
                    minWidth: 0,
                    height: 18,
                    borderRadius: "50%",
                    border: "none",
                    bgcolor: isActive ? "rgba(255,255,255,0.2)" : "grey.100",
                    color: isActive ? "inherit" : "grey.600",
                    opacity: 0,
                    overflow: "hidden",
                    flexShrink: 0,
                    pointerEvents: "none",
                    transition:
                      "width 0.15s ease, min-width 0.15s ease, opacity 0.15s ease, margin 0.15s ease",
                    cursor: isLoading ? "not-allowed" : "pointer",
                    "&:hover": {
                      bgcolor: isActive ? "rgba(255,255,255,0.35)" : "grey.200",
                      color: isActive ? "inherit" : "grey.900",
                    },
                  }}
                >
                  <Pencil size={10} />
                </Box>
              ) : null}
            </Box>
          );
        })}

        {canEdit ? (
          <Box
            component="button"
            type="button"
            onClick={onOpenCreateFolder}
            disabled={isLoading}
            aria-label={t(GALLERY_FOLDERS_NEW_INLINE)}
            sx={getCreateFolderButtonSx()}
          >
            <FolderPlus size={14} />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default GalleryFolderBar;
